import { useState, useReducer, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue } from 'recoil';
import { TzWalletSystem } from '~/types/common';

import fp from 'lodash/fp';

import validId from '../utils/validId';

import tzWalletUserQuery from '~/restApi/tzWalletUserQuery';

enum QueryUserError {
  NOT_USE_TEST_ID = 'NOT_USE_TEST_ID',
  INVALID_PARAMS = 'INVALID_PARAMS',
  INVALID_USER = 'INVALID_USER',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
}

export default function useWalletForm() {
  const auth = useRecoilValue(authAtom);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [walletState, setWalletState] = useState<{
    state: { system: TzWalletSystem; id: string };
    errors: { system: string; id: string };
  }>({
    state: {
      system: TzWalletSystem.HOSPITAL,
      id: '',
    },
    errors: {
      system: '',
      id: '',
    },
  });

  const handleReset = () => {
    setWalletState({
      state: {
        system: TzWalletSystem.HOSPITAL,
        id: '',
      },
      errors: {
        system: '',
        id: '',
      },
    });
  };

  const handleSendQueryRequest = useCallback(
    async (system: TzWalletSystem, id: string) => {
      if (!auth) {
        setWalletState((prev) => ({ ...prev, errors: { ...prev.errors, id: '請先登入' } }));
        return;
      }

      setIsLoading(true);
      const authToken = await auth.getIdToken();
      try {
        await tzWalletUserQuery(id, system, authToken);
        handleReset();
        toast({ title: '已成功綁定', status: 'success', position: 'top' });
      } catch (err: any) {
        const result = err.response.data;
        const code = result?.code || QueryUserError.INTERNAL_SERVER_ERROR;
        let errorMessage = '';
        switch (code) {
          case QueryUserError.INVALID_PARAMS:
            errorMessage = '請輸入正確的身份證號';
            break;
          case QueryUserError.INVALID_USER:
            errorMessage = '此身份證號不符合使用慈濟錢包資格';
            break;
          case QueryUserError.NOT_USE_TEST_ID:
            errorMessage = '未使用測試ID';
            break;
          case QueryUserError.INTERNAL_SERVER_ERROR:
            errorMessage = '系統錯誤，請聯絡客服';
            break;
        }
        toast({ title: '驗證失敗', status: 'error', position: 'top' });
        setWalletState((prev) => ({ ...prev, errors: { ...prev.errors, id: errorMessage } }));
      } finally {
        setIsLoading(false);
      }
    },
    [toast, auth],
  );

  useEffect(() => {
    if (walletState.state.system && walletState.state.id.length === 10) {
      const validIdResult = validId(walletState.state.id);
      if (!validIdResult) {
        setWalletState((prev) => ({
          ...prev,
          errors: { ...prev.errors, id: '請輸入正確的身份證號' },
        }));
        return;
      }
      handleSendQueryRequest(walletState.state.system, walletState.state.id);
    }
  }, [walletState.state]);

  const handleChangeId = (id: string) => {
    setWalletState((prev) => ({
      state: {
        ...prev.state,
        id,
      },
      errors: {
        ...prev.errors,
        id: '',
      },
    }));
  };

  const handleChangeSystem = (system: TzWalletSystem) => {
    setWalletState((prev) => ({
      ...prev,
      state: {
        ...prev.state,
        system,
      },
    }));
  };

  return {
    walletState,
    isLoading,
    handleReset,
    handleChangeId,
    handleChangeSystem,
  };
}
